import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './../../Providers/AuthProvider'

/**
 * Obtiene o inicializa la huella digital única persistente de este dispositivo.
 */
const getDeviceId = () => {
    try {
        let id = localStorage.getItem('_intk_device_id');
        if (!id) {
            id = 'dev_' + Math.random().toString(36).substring(2, 12) + '_' + Date.now();
            localStorage.setItem('_intk_device_id', id);
        }
        return id;
    } catch {
        return 'dev_unknown';
    }
};

/**
 * Obtiene el estado de bloqueo local de este dispositivo.
 */
const getDeviceLockout = () => {
    try {
        const raw = localStorage.getItem('_intk_dev_lockout');
        return raw ? JSON.parse(raw) : { failedAttempts: 0, lockedUntil: null };
    } catch {
        return { failedAttempts: 0, lockedUntil: null };
    }
};

/**
 * Guarda el estado de bloqueo local de este dispositivo.
 */
const saveDeviceLockout = (data) => {
    try {
        localStorage.setItem('_intk_dev_lockout', JSON.stringify(data));
    } catch (e) {
        console.warn('Error guardando estado de bloqueo:', e);
    }
};

const Login = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { login, isAuthenticated, role } = useAuth()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState(null)
    const [expiredNotice, setExpiredNotice] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    // Asegurar inicialización de huella digital de dispositivo
    useEffect(() => {
        getDeviceId();
    }, []);

    // Detectar si el usuario fue redirigido por expiración de sesión
    useEffect(() => {
        try {
            const storedMsg = sessionStorage.getItem('_session_expired_msg');
            if (storedMsg) {
                setExpiredNotice(storedMsg);
                sessionStorage.removeItem('_session_expired_msg');
            }
        } catch (e) {
            console.warn(e);
        }
    }, []);

    const redirectUser = (userRole) => {
        const from = location.state?.from?.pathname
        if (from && from !== '/login' && from !== '/admin') {
            navigate(from, { replace: true })
            return
        }

        if (userRole === 'sound_operator') {
            navigate('/admin/playlist/cabina', { replace: true })
        } else {
            navigate('/admin/dashboard', { replace: true })
        }
    }

    useEffect(() => {
        if (isAuthenticated) {
            const userRole = (role || localStorage.getItem('role') || '').toLowerCase()
            redirectUser(userRole)
        }
    }, [isAuthenticated, role, navigate])

    // Función principal para iniciar sesión con protección por dispositivo
    const handleLogin = async (event) => {
        if (event) {
            event.preventDefault()
        }

        try {
            setError(null)

            // 1. Validar si ESTE dispositivo específico se encuentra bloqueado
            const lockout = getDeviceLockout();
            if (lockout.lockedUntil && Date.now() < lockout.lockedUntil) {
                const remainingMinutes = Math.max(1, Math.ceil((lockout.lockedUntil - Date.now()) / (60 * 1000)));
                setError(`Este dispositivo ha sido bloqueado temporalmente por múltiples intentos fallidos. Intenta nuevamente en ${remainingMinutes} minuto(s).`);
                return;
            }

            // 2. Validaciones básicas de campos
            if (!username.trim()) {
                setError('Escribe tu usuario')
                return
            }

            if (!password.trim()) {
                setError('Escribe tu contraseña')
                return
            }

            setIsLoading(true)

            // 3. Invocar autenticación
            const response = await login(username, password)

            if (response.success) {
                // Éxito: restablecer intentos fallidos para este dispositivo
                saveDeviceLockout({ failedAttempts: 0, lockedUntil: null });

                const userRole = (
                    response.data?.user?.role ||
                    response.data?.role ||
                    role ||
                    localStorage.getItem('role') ||
                    ''
                ).toLowerCase()

                redirectUser(userRole)
            } else {
                // Fallo: registrar intento fallido exclusivo en este dispositivo
                const currentAttempts = (lockout.failedAttempts || 0) + 1;
                let nextLockedUntil = null;

                if (currentAttempts >= 5) {
                    // 5 intentos fallidos -> Bloquea este dispositivo por 5 minutos
                    nextLockedUntil = Date.now() + 5 * 60 * 1000;
                }

                saveDeviceLockout({
                    failedAttempts: currentAttempts,
                    lockedUntil: nextLockedUntil,
                });

                // Retardo progresivo para frenar robots automáticos (1s, 2s, 3s, hasta 8s máx)
                const delayMs = Math.min(currentAttempts * 1000, 8000);
                await new Promise((resolve) => setTimeout(resolve, delayMs));

                if (nextLockedUntil) {
                    setError('Este dispositivo ha sido bloqueado temporalmente por 5 minutos tras 5 intentos fallidos consecutivos.');
                } else {
                    setError(response.message || 'Usuario o contraseña incorrectos');
                }
            }
        } catch (err) {
            console.error(err)
            setError('Ha habido un error al intentar iniciar sesión. Inténtalo nuevamente o inténtalo más tarde.')
        } finally {
            setIsLoading(false)
        }
    }

    // Detectar tecla Enter en el formulario
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            if (!isLoading) {
                handleLogin()
            }
        }
    }

    return (
        <main className="container Login">
            <div className="row Login__Container">
                <div className="row">
                    <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />

                    <h1 className="mt-2 text-center fs-4">Iniciar sesión</h1>

                    {/* Aviso de expiración de sesión por inactividad */}
                    {expiredNotice && (
                        <div className="alert alert-warning text-center small mt-3 py-2" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {expiredNotice}
                        </div>
                    )}

                    <form className="mt-4" onSubmit={handleLogin} onKeyDown={handleKeyDown}>
                        <div className="mb-3">
                            <input
                                type="text"
                                className="form-control"
                                id="txtUser"
                                placeholder="Usuario"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="username"
                                disabled={isLoading}
                            />
                        </div>

                        <div className="mb-3">
                            <input
                                type="password"
                                className="form-control"
                                id="txtPassword"
                                placeholder="Contraseña"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                disabled={isLoading}
                            />
                        </div>

                        <button 
                            type="button" 
                            className="Btn__Adsl--Red" 
                            onClick={handleLogin} 
                            disabled={isLoading}
                        >
                            {isLoading ? 'Iniciando...' : 'Iniciar sesión'}
                        </button>

                        {error && error !== '' && (
                            <p className="text-center text-danger mt-3">{error}</p>
                        )}
                    </form>
                </div>
            </div>
        </main>
    )
}

export { Login };